/**
 * Minimal i18n: English + Afrikaans. Each feature area owns one string file in
 * ./strings and registers it here. Keys are namespaced "<ns>.<key>".
 * test/i18n.test.ts checks every key exists in both languages.
 */
import { useSyncExternalStore } from 'react';
import type { Locale } from '@dropstack/core';
import { COMMON } from './strings/common.ts';
import { SHELL } from './strings/shell.ts';
import { GAME } from './strings/game.ts';
import { HANDWRITING } from './strings/handwriting.ts';

export type Dict = Record<string, string>;
export interface StringTable {
  en: Dict;
  af: Dict;
}

export const TABLES: Record<string, StringTable> = { common: COMMON, shell: SHELL, game: GAME, hw: HANDWRITING };

let locale: Locale = 'en';
const listeners = new Set<() => void>();

export function setLocale(l: Locale): void {
  if (l === locale) return;
  locale = l;
  document.documentElement.lang = l;
  listeners.forEach((f) => f());
}

export const getLocale = (): Locale => locale;

/** Translate "ns.key" with {param} substitution. Falls back to English, then to the key itself. */
export function t(key: string, params?: Record<string, string | number>, l: Locale = locale): string {
  const dot = key.indexOf('.');
  const table = TABLES[key.slice(0, dot)];
  const k = key.slice(dot + 1);
  let s = table?.[l][k] ?? table?.en[k] ?? key;
  if (params) for (const [p, v] of Object.entries(params)) s = s.replaceAll(`{${p}}`, String(v));
  return s;
}

/** React hook: re-renders on locale change and returns the bound translator. */
export function useT(): typeof t {
  useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => locale,
  );
  return t;
}
